import { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { PlusIcon, XIcon } from "lucide-react";

import {
  useBulkUpdateItemLabels,
  useCreateItemLabel,
  useItemLabels,
  type Item,
  type ItemLabel,
} from "@/api/items";
import { Badge } from "@/components/ui/badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const DEFAULT_LABEL_COLOR = "#a1a1aa"; // zinc-400

export interface BulkLabelsDialogProps {
  items: Item[];
  onClose: () => void;
}

export function BulkLabelsDialog({ items, onClose }: BulkLabelsDialogProps) {
  const { t } = useTranslation();
  const itemIds = useMemo(() => items.map((i) => i.id), [items]);

  const allLabelsQuery = useItemLabels();
  const allLabels = allLabelsQuery.data ?? [];
  const bulkUpdate = useBulkUpdateItemLabels();
  const createLabel = useCreateItemLabel();

  const [query, setQuery] = useState("");

  // Union of labels across selected items, alpha sorted.
  const unionLabels = useMemo<ItemLabel[]>(() => {
    const byId = new Map<number, ItemLabel>();
    for (const item of items) {
      for (const l of item.labels ?? []) byId.set(l.id, l);
    }
    return Array.from(byId.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [items]);

  const unionIds = new Set(unionLabels.map((l) => l.id));
  const trimmed = query.trim();
  const availableLabels = allLabels
    .filter((l) => !unionIds.has(l.id))
    .filter(
      (l) =>
        trimmed.length === 0 ||
        l.name.toLowerCase().includes(trimmed.toLowerCase()),
    );

  const exactMatch = allLabels.some(
    (l) => l.name.toLowerCase() === trimmed.toLowerCase(),
  );
  const showCreate =
    trimmed.length > 0 && !exactMatch && !createLabel.isPending;

  const handleRemove = (label: ItemLabel) =>
    bulkUpdate.mutate({ itemIds, add: [], remove: [label.id] });

  const handleAdd = (label: ItemLabel) => {
    bulkUpdate.mutate({ itemIds, add: [label.id], remove: [] });
    setQuery("");
  };

  const handleCreate = () => {
    createLabel.mutate(
      { name: trimmed, color: DEFAULT_LABEL_COLOR },
      {
        onSuccess: (newLabel) => {
          bulkUpdate.mutate({ itemIds, add: [newLabel.id], remove: [] });
          setQuery("");
        },
      },
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("items.editTagsTitle")}</DialogTitle>
          <DialogDescription>
            {t("items.editTagsDescription", { count: items.length })}
          </DialogDescription>
        </DialogHeader>

        <div className="bg-muted/30 flex min-h-12 flex-wrap gap-1.5 rounded-md border p-3">
          {unionLabels.map((l) => (
            <Badge key={l.id} variant="outline" className="gap-1">
              {l.name}
              <button
                type="button"
                onClick={() => handleRemove(l)}
                aria-label={t("items.bulkLabels.removeLabel", { name: l.name })}
                className="hover:bg-muted ml-1 rounded"
              >
                <XIcon className="size-3" />
              </button>
            </Badge>
          ))}
          {unionLabels.length === 0 && (
            <span className="text-muted-foreground text-xs">
              {t("items.editTagsNoLabels")}
            </span>
          )}
        </div>

        <Command>
          <CommandInput
            placeholder={t("items.editTagsAddPlaceholder")}
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            <CommandEmpty>{t("common.noResults")}</CommandEmpty>
            <CommandGroup>
              {availableLabels.map((l) => (
                <CommandItem key={l.id} onSelect={() => handleAdd(l)}>
                  {l.name}
                </CommandItem>
              ))}
              {showCreate && (
                <CommandItem onSelect={handleCreate}>
                  <PlusIcon className="text-muted-foreground mr-2 size-4" />
                  {t("items.editTagsCreate", { name: trimmed })}
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
